import { ApolloCache, useMutation } from '@apollo/client'
import { Formik } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Row } from 'components/v2/Row'
import { Select } from 'components/v2/Select'
import { WarningNotification } from 'components/WarningNotification'
import { ACTIVATE_VEHICLE, DEACTIVATE_VEHICLE } from 'features/Vehicles/mutations-legacy'
import { IChangeVehicleStatusForm } from 'features/Vehicles/types'
import { GTTVehicle, GTVehicleStatus } from 'gql/types.generated'
import { getUnprocessableErrors, safeFormatMessage } from '@10xlabs/utils'
import { DeactivateInputs } from 'features/Vehicles/components/ChangeVehicleStatusModal/DeactivateInputs'
import { vehicleStatusMessages } from 'features/Vehicles/components/Status/messages'
import { schema } from './schema'
import { MARK_VEHICLE_OUT_OF_SERVICE } from 'features/Vehicles/mutations'

const messages = defineMessages({
  statusPlaceholder: {
    id: 'vehicles.changeStatus.status.placeholder',
    defaultMessage: 'Select status'
  }
})

const STATUS_LIST = {
  [GTVehicleStatus.Active]: [GTVehicleStatus.Inactive, GTVehicleStatus.OutOfService],
  [GTVehicleStatus.Inactive]: [GTVehicleStatus.Active],
  [GTVehicleStatus.OutOfService]: [GTVehicleStatus.Active],
  [GTVehicleStatus.Assigned]: [],
  [GTVehicleStatus.Unassigned]: []
}

interface IChangeVehicleStatusModalProps {
  vehicle: GTTVehicle
  closeModal: () => void
}

export const ChangeVehicleStatusModal = ({ vehicle, closeModal }: IChangeVehicleStatusModalProps): ReactElement => {
  const intl = useIntl()

  const updateCachedVehicleStatus = (cache: ApolloCache<unknown>, status: GTVehicleStatus): void => {
    cache.modify({
      id: cache.identify(vehicle),
      fields: {
        status: () => status
      }
    })
  }
  const [activateVehicle, { loading: activateVehicleLoading, error: activateVehicleError }] = useMutation(
    ACTIVATE_VEHICLE,
    {
      update: (cache): void => {
        updateCachedVehicleStatus(cache, GTVehicleStatus.Active)
      }
    }
  )
  const [deactivateVehicle, { loading: deactivateVehicleLoading, error: deactivateVehicleError }] = useMutation(
    DEACTIVATE_VEHICLE,
    {
      update: (cache): void => {
        updateCachedVehicleStatus(cache, GTVehicleStatus.Inactive)
      }
    }
  )
  const [markVehicleOutOfService, { loading: markVehicleOutOfServiceLoading, error: markVehicleOutOfServiceError }] =
    useMutation(MARK_VEHICLE_OUT_OF_SERVICE, {
      update: (cache): void => {
        updateCachedVehicleStatus(cache, GTVehicleStatus.OutOfService)
      }
    })

  const handleActivate = async (): Promise<void> => {
    try {
      await activateVehicle({
        variables: {
          id: vehicle.id
        }
      })
      closeModal()
    } catch (error) {
      // Do nothing
    }
  }

  const handleDeactivate = async (values: IChangeVehicleStatusForm): Promise<void> => {
    try {
      await deactivateVehicle({
        variables: {
          id: vehicle.id,
          input: {
            reason: values.reason
          }
        }
      })
      closeModal()
    } catch (error) {
      // Do nothing
    }
  }

  const handleOutOfService = async (): Promise<void> => {
    try {
      await markVehicleOutOfService({
        variables: {
          input: {
            id: vehicle.id
          }
        }
      })
      closeModal()
    } catch (error) {
      // Do nothing
    }
  }

  const handleSubmit = async (values: IChangeVehicleStatusForm): Promise<void> => {
    switch (values.status) {
      case GTVehicleStatus.Active:
        return handleActivate()
      case GTVehicleStatus.Inactive:
        return handleDeactivate(values)
      case GTVehicleStatus.OutOfService:
        return handleOutOfService()
      default:
        return
    }
  }

  const initialValues: IChangeVehicleStatusForm = {
    status: '',
    reason: ''
  }

  const loading = activateVehicleLoading || deactivateVehicleLoading || markVehicleOutOfServiceLoading
  const error = activateVehicleError || deactivateVehicleError || markVehicleOutOfServiceError

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage
            id="vehicles.changeStatus.title"
            defaultMessage="Change status {vehicleName}"
            values={{
              vehicleName: vehicle.name
            }}
          />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {({ handleSubmit, values, handleChange, handleBlur, touched, errors }): ReactElement => {
          const routeServicesNb = vehicle?.routeServices?.length || 0

          return (
            <form onSubmit={handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <Select
                      id="status"
                      value={values.status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.status}
                      errors={errors.status}
                      label={<FormattedMessage id="vehicle.changeStatus.newStatus.label" defaultMessage="New Status" />}
                    >
                      <option value="">{intl.formatMessage(messages.statusPlaceholder)}</option>
                      {STATUS_LIST[vehicle.status].map((status): ReactElement => {
                        return (
                          <option key={status} value={status}>
                            {safeFormatMessage({ intl, messages: vehicleStatusMessages, key: status })}
                          </option>
                        )
                      })}
                    </Select>
                  </Column>
                </Row>
                {values.status === GTVehicleStatus.Inactive && <DeactivateInputs />}
                <Row>
                  <Column>
                    <WarningNotification>
                      <FormattedMessage
                        id="vehicle.deactivate.routeServices.desc"
                        defaultMessage="Are you sure you want to change the vehicle status? {routeServicesNb, plural,
                          one {1 service}
                          other {# services}
                        } will be affected."
                        values={{
                          routeServicesNb
                        }}
                      />
                    </WarningNotification>
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="vehicle.deactivate.error"
                        defaultMessage="There was an error while changing the vehicle status. Please try again."
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="modal.actions.close" defaultMessage="No, close" />
                  </Button>
                  <Button kind="primary" loading={loading}>
                    <FormattedMessage id="vehicle.deactivate.submit" defaultMessage="Change status" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
